import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, Pencil, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { defaultPaginationMeta } from '@/lib/api/masterCatalog'
import { exportRowsToCsv } from '@/lib/csvExport'
import { cn } from '@/lib/utils'

const EMPTY_EXTRA_PARAMS = Object.freeze({})

/**
 * @typedef {object} MasterServerTableColumn
 * @property {string} key
 * @property {string} label
 * @property {boolean} [sortable]
 * @property {(row: object) => import('react').ReactNode} [render]
 * @property {string} [className]
 */

/**
 * @typedef {object} MasterServerTableExportColumn
 * @property {string} key
 * @property {string} label
 * @property {(row: object) => string | number | boolean | null | undefined} getValue
 */

/**
 * @param {object} props
 * @param {string} [props.title]
 * @param {MasterServerTableColumn[]} props.columns
 * @param {(params: { page: number, per_page: number, q: string, sort_by?: string, sort_dir?: 'asc' | 'desc' }) => Promise<{ items: object[], meta: import('@/types/api').PaginationMeta }>} props.fetchPage
 * @param {(row: object) => string | number} props.getRowKey
 * @param {(row: object) => void} [props.onEdit]
 * @param {string} [props.editLabel='Edit']
 * @param {MasterServerTableExportColumn[]} [props.exportColumns]
 * @param {string} [props.exportFilename='export.csv']
 * @param {(params: Record<string, unknown>) => Promise<object[]>} [props.fetchAllForExport]
 * @param {string} [props.emptyLabel='No records yet.']
 * @param {string} [props.searchPlaceholder='Search…']
 * @param {number} [props.perPage=15]
 * @param {Record<string, unknown>} [props.extraParams]
 * @param {number} [props.reloadKey=0]
 * @param {import('react').ReactNode} [props.toolbar]
 */
export function MasterServerTable({
  title,
  columns,
  fetchPage,
  getRowKey,
  onEdit,
  editLabel = 'Edit',
  exportColumns,
  exportFilename = 'export.csv',
  fetchAllForExport,
  emptyLabel = 'No records yet.',
  searchPlaceholder = 'Search…',
  perPage = 15,
  extraParams,
  reloadKey = 0,
  toolbar = null,
}) {
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(defaultPaginationMeta())
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sortBy, setSortBy] = useState('')
  const [sortDir, setSortDir] = useState('asc')
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const resolvedExtraParams = extraParams ?? EMPTY_EXTRA_PARAMS
  const extraParamsKey = useMemo(
    () => JSON.stringify(resolvedExtraParams),
    [resolvedExtraParams],
  )

  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [q])

  useEffect(() => {
    setPage(1)
  }, [debouncedQ, sortBy, sortDir, reloadKey, extraParamsKey])

  useEffect(() => {
    let active = true

    async function load() {
      setLoading(true)
      setError('')

      const params = {
        page,
        per_page: perPage,
        q: debouncedQ || undefined,
        ...resolvedExtraParams,
      }

      if (sortBy) {
        params.sort_by = sortBy
        params.sort_dir = sortDir
      }

      try {
        const result = await fetchPageRef.current(params)
        if (!active) return
        setItems(Array.isArray(result.items) ? result.items : [])
        setMeta(defaultPaginationMeta(result.meta ?? {}))
      } catch (err) {
        if (!active) return
        setError(err?.response?.data?.message ?? 'Unable to load records.')
        setItems([])
        setMeta(defaultPaginationMeta())
      } finally {
        if (active) setLoading(false)
      }
    }

    load()

    return () => {
      active = false
    }
  }, [page, debouncedQ, sortBy, sortDir, reloadKey, extraParamsKey, perPage])

  function toggleSort(columnKey) {
    if (sortBy === columnKey) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(columnKey)
    setSortDir('asc')
  }

  async function handleExport() {
    if (!exportColumns?.length) return
    setExporting(true)
    try {
      const rows = fetchAllForExport
        ? await fetchAllForExport({ q: debouncedQ || undefined, ...resolvedExtraParams })
        : items
      exportRowsToCsv(exportColumns, rows, exportFilename)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="card-surface overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          {title ? (
            <p className="text-sm font-semibold text-foreground">{title}</p>
          ) : null}
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={searchPlaceholder}
              className={cn(
                'h-10 w-full rounded-xl border border-border/80 bg-card pl-9 pr-3 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
          {toolbar}
        </div>
        {exportColumns?.length ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || loading}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'CSV'}
          </Button>
        ) : null}
      </div>

      {error ? (
        <p className="px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              {columns.map((column) => (
                <th key={column.key} className={cn('px-4 py-3 font-semibold', column.className)}>
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      {column.label}
                      {sortBy === column.key ? (
                        <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>
                      ) : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
              {onEdit ? <th className="px-4 py-3 font-semibold text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (onEdit ? 1 : 0)}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (onEdit ? 1 : 0)}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={getRowKey(row)}
                  className="border-b border-border/40 transition-colors hover:bg-muted/20"
                >
                  {columns.map((column) => (
                    <td key={column.key} className={cn('px-4 py-3', column.className)}>
                      {column.render ? column.render(row) : String(row[column.key] ?? '—')}
                    </td>
                  ))}
                  {onEdit ? (
                    <td className="px-4 py-3 text-right">
                      <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(row)}>
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">{editLabel}</span>
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-4 py-3 text-sm text-muted-foreground">
        <span>
          {meta.total > 0
            ? `Showing page ${meta.current_page} of ${meta.last_page} (${meta.total} total)`
            : 'No records'}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={loading || meta.current_page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((current) => Math.min(meta.last_page, current + 1))}
            disabled={loading || meta.current_page >= meta.last_page}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
